/**
 * Modal Component
 *
 * Reusable modal/dialog base component with backdrop, animations, and accessibility features.
 * Provides consistent modal patterns throughout the application.
 *
 * @example
 * <Modal isOpen={showModal()} onClose={handleClose} title="Confirm Action">
 *   <p>Are you sure you want to proceed?</p>
 * </Modal>
 *
 * @example
 * <Modal
 *   isOpen={showModal()}
 *   onClose={handleClose}
 *   size="lg"
 *   footer={
 *     <div class="flex gap-2">
 *       <Button onClick={handleConfirm}>Confirm</Button>
 *       <Button onClick={handleClose}>Cancel</Button>
 *     </div>
 *   }
 * >
 *   <p>Modal content goes here</p>
 * </Modal>
 */

import { Component, JSX, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useEscapeKey } from "../hooks/useEscapeKey";

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title (string or JSX element) */
  title?: string | JSX.Element;
  /** Modal content */
  children: JSX.Element;
  /** Optional footer content */
  footer?: JSX.Element;
  /** Modal size variant */
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** Allow closing by clicking backdrop */
  closeOnBackdrop?: boolean;
  /** Allow closing with Escape key */
  closeOnEscape?: boolean;
  /** Show close button (X) in header */
  showCloseButton?: boolean;
  /** Additional CSS classes for modal container */
  class?: string;
  /** ARIA label for dialog */
  "aria-label"?: string;
}

/**
 * Modal - Reusable modal/dialog component
 *
 * Features:
 * - Portal rendering at document root for proper z-index layering
 * - Body scroll lock when modal is open
 * - Backdrop click and Escape key handling
 * - Smooth fade and scale animations
 * - Multiple size options from small dialogs to full-screen
 * - Accessible with ARIA dialog role and focus management
 * - Customizable header, body, and footer sections
 *
 * CRITICAL: Never destructure props - use props.propName for reactivity
 */
export const Modal: Component<ModalProps> = (props) => {
  // Default values
  const size = () => props.size || "md";
  const closeOnBackdrop = () => props.closeOnBackdrop ?? true;
  const closeOnEscape = () => props.closeOnEscape ?? true;
  const showCloseButton = () => props.showCloseButton ?? true;

  useEscapeKey(() => props.onClose(), {
    when: () => closeOnEscape() && props.isOpen,
    target: "document",
  });

  // Handle backdrop click
  const handleBackdropClick = () => {
    if (closeOnBackdrop()) {
      props.onClose();
    }
  };

  useBodyScrollLock(() => props.isOpen);

  // Size classes for modal container
  const sizeClasses = () => {
    switch (size()) {
      case "sm":
        return "max-w-sm";
      case "md":
        return "max-w-md";
      case "lg":
        return "max-w-2xl";
      case "xl":
        return "max-w-4xl";
      case "full":
        return "max-w-4xl w-full max-h-[90vh]";
      default:
        return "max-w-md";
    }
  };

  return (
    <Show when={props.isOpen}>
      <Portal>
        {/* Backdrop */}
        <div
          class="animate-fade-in fixed inset-0 z-60 bg-black/80 backdrop-blur-sm"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />

        {/* Modal Container */}
        <div
          class="fixed inset-0 z-70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={
            props["aria-label"] ||
            (typeof props.title === "string" ? props.title : "Dialog")
          }
        >
          <div
            class={`rounded-xl border-2 border-cyan-500 bg-gray-900 shadow-2xl shadow-cyan-500/50 ${sizeClasses()} animate-scale-in flex w-full flex-col ${props.class || ""}`}
            onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking modal content
          >
            {/* Header */}
            <Show when={props.title || showCloseButton()}>
              <div class="to-magenta-500/10 flex items-center justify-between border-b border-cyan-500/30 bg-gradient-to-r from-cyan-500/10 px-6 py-4">
                <Show when={props.title}>
                  <h2
                    class="text-2xl font-bold"
                    classList={{
                      "bg-gradient-to-r from-cyan-400 to-magenta-400 bg-clip-text text-transparent":
                        typeof props.title === "string",
                    }}
                  >
                    {props.title}
                  </h2>
                </Show>
                <Show when={showCloseButton()}>
                  <button
                    onClick={() => props.onClose()}
                    class="ml-auto rounded-lg p-2 text-gray-400 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                    aria-label="Close modal"
                  >
                    <svg
                      class="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </Show>
              </div>
            </Show>

            {/* Body */}
            <div
              class="px-6 py-4"
              classList={{
                "flex-1 overflow-y-auto": size() === "full",
              }}
            >
              {props.children}
            </div>

            {/* Footer */}
            <Show when={props.footer}>
              <div class="border-t border-cyan-500/30 px-6 py-4">
                {props.footer}
              </div>
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
};
